import { supabase } from "@/integrations/supabase/client";

export type AccountRole =
  | "cash_bank" | "customers_ar" | "vendors_ap" | "fixed_assets"
  | "accumulated_depr" | "inventory" | "prepaid_expenses" | "accrued_expenses"
  | "tax_payable" | "salaries" | "cip" | "sales_revenue" | "cogs"
  | "employee_advances" | "employee_custody";

export interface AccountRow {
  id: string;
  parent_id: string | null;
  code: string;
  name_ar: string;
  name_en: string | null;
  account_type: "asset" | "liability" | "equity" | "revenue" | "expense";
  balance_nature: "debit" | "credit";
  is_group: boolean;
  status: string;
  expense_kind?: "opex" | "ga" | "cogs" | null;
  expense_property?: "depreciation" | "salaries" | "taxes" | "accrual" | null;
  account_role?: AccountRole | null;
  is_current?: boolean | null;
  cash_flow_activity?: "operating" | "investing" | "financing" | null;
}


export interface LedgerLine {
  id: string;
  entry_id: string;
  entry_number: string;
  entry_date: string;
  description: string | null;
  line_description: string | null;
  account_id: string;
  debit: number;
  credit: number;
  cost_center_id: string | null;
  source?: string | null;
  source_ref_id?: string | null;
}

export interface AccountTotals {
  opening_debit: number;
  opening_credit: number;
  period_debit: number;
  period_credit: number;
}

const numeric = (v: unknown) => Number(v ?? 0);
const REPORT_QUERY_PAGE_SIZE = 1000;

export async function fetchAccounts(companyId: string): Promise<AccountRow[]> {
  const PAGE = 1000;
  const out: AccountRow[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("accounts")
      .select("id, parent_id, code, name_ar, name_en, account_type, balance_nature, is_group, status, expense_kind, expense_property, account_role, is_current, cash_flow_activity")
      .eq("company_id", companyId)
      .order("code")
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data || []) as AccountRow[];
    out.push(...rows);
    if (rows.length < PAGE) break;
  }
  return out;
}

interface LinesQuery {
  companyId: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  accountIds?: string[] | null;
  costCenterIds?: string[] | null;
}

/**
 * All report reads go through server-side RPCs (SECURITY DEFINER, membership
 * checked inside the function). The RPCs apply the exact same rules the old
 * client-side pipeline did:
 *   - only posted, non-deleted entries
 *   - the company's opening-balance-date rule (manual entries dated before it
 *     are excluded; automatic sources stay visible)
 *   - optional account / cost-center narrowing
 * Aggregation happens in Postgres, so a report is now ONE round-trip instead of
 * a paginated fetch of every journal line.
 */
const rpc = (fn: string, args: Record<string, unknown>) =>
  (supabase.rpc as any)(fn, args);

/** Error raised when a report is requested without a usable signed-in session. */
export class ReportSessionError extends Error {
  constructor() {
    super("report_session_missing");
    this.name = "ReportSessionError";
  }
}

const isPermissionError = (e: any) =>
  e?.code === "42501" || /permission denied|JWT|Access denied/i.test(String(e?.message ?? ""));

/**
 * Report RPCs are granted to `authenticated` only. Make sure a session is
 * attached before sending (local read, no network unless the token is due),
 * so a request never goes out as `anon` during a token refresh.
 */
export async function ensureReportSession(): Promise<void> {
  const { data } = await supabase.auth.getSession();
  if (data.session?.access_token) return;
  const { data: refreshed } = await supabase.auth.refreshSession();
  if (!refreshed.session?.access_token) throw new ReportSessionError();
}

/** PostgREST caps rows per response; RPC rows are paged the same way. */
async function rpcAll<T>(fn: string, args: Record<string, unknown>): Promise<T[]> {
  if (!args.p_company_id) return [];
  await ensureReportSession();
  const out: T[] = [];
  for (let from = 0; ; from += REPORT_QUERY_PAGE_SIZE) {
    let { data, error } = await rpc(fn, args).range(from, from + REPORT_QUERY_PAGE_SIZE - 1);
    if (error && isPermissionError(error)) {
      // Token may have been mid-rotation: refresh once and retry this page.
      const { data: r } = await supabase.auth.refreshSession();
      if (!r.session?.access_token) throw new ReportSessionError();
      ({ data, error } = await rpc(fn, args).range(from, from + REPORT_QUERY_PAGE_SIZE - 1));
    }
    if (error) throw error;
    const rows = (data || []) as T[];
    out.push(...rows);
    if (rows.length < REPORT_QUERY_PAGE_SIZE) break;
  }
  return out;
}

/**
 * Paginates any Supabase table query by calling `.range()` repeatedly.
 * Pass a *factory* lambda that returns a fresh query builder each call
 * (do NOT pass a pre-built query – the same builder cannot be reused).
 *
 * Usage:
 *   const rows = await fetchAllRows(() =>
 *     supabase.from("table").select("…").eq("company_id", id)
 *   );
 */
export async function fetchAllRows(buildQuery: () => any): Promise<any[]> {
  const PAGE = 1000;
  const all: any[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await buildQuery().range(from, from + PAGE - 1);
    if (error) throw error;
    const rows: any[] = data || [];
    all.push(...rows);
    if (rows.length < PAGE) break;
  }
  return all;
}



/**
 * Fetch posted journal-entry lines for a company.
 * Exported so lightweight consumers (e.g. dashboard health card) reuse the
 * same pipeline as the full reports.
 */
export async function getPostedLines(q: LinesQuery): Promise<LedgerLine[]> {
  return fetchLines(q);
}

async function fetchLines(q: LinesQuery): Promise<LedgerLine[]> {
  const rows = await rpcAll<any>("rpc_ledger_lines", {
    p_company_id: q.companyId,
    p_date_from: q.dateFrom ?? null,
    p_date_to: q.dateTo ?? null,
    p_account_ids: q.accountIds && q.accountIds.length ? q.accountIds : null,
    p_cost_center_ids: q.costCenterIds && q.costCenterIds.length ? q.costCenterIds : null,
  });

  return rows.map((r: any) => ({
    id: r.id,
    entry_id: r.entry_id,
    entry_number: r.entry_number,
    entry_date: r.entry_date,
    description: r.description,
    line_description: r.line_description,
    account_id: r.account_id,
    debit: numeric(r.debit),
    credit: numeric(r.credit),
    cost_center_id: r.cost_center_id,
    source: r.source ?? null,
    source_ref_id: r.source_ref_id ?? null,
  }));
}

/** Get totals per account split into opening (< dateFrom) and period [dateFrom..dateTo]. */
// perf_large_data (dashboard): companies whose no-cost-center totals calls are
// coalesced into one rpc_account_totals_batch request per tick. Window i of the
// batch is proven identical to rpc_account_totals_for_report (scripts/parity/dashboard).
const totalsBatchCompanies = new Set<string>();
export function setTotalsBatching(companyId: string, on: boolean) {
  if (on) totalsBatchCompanies.add(companyId); else totalsBatchCompanies.delete(companyId);
}
type Pending = { key: string; from: string | null; to: string | null; resolve: (r: any[]) => void; reject: (e: unknown) => void };
const pendingByCompany = new Map<string, Pending[]>();

async function flushTotalsBatch(companyId: string) {
  const items = pendingByCompany.get(companyId) || [];
  pendingByCompany.delete(companyId);
  if (!items.length) return;
  const keys = Array.from(new Set(items.map((i) => i.key)));
  const wins = keys.map((k) => items.find((i) => i.key === k)!);
  try {
    await ensureReportSession();
    const all: any[] = [];
    for (let from = 0; ; from += REPORT_QUERY_PAGE_SIZE) {
      const { data, error } = await (supabase as any).rpc("rpc_account_totals_batch", {
        p_company_id: companyId,
        p_froms: wins.map((w) => w.from),
        p_tos: wins.map((w) => w.to),
        p_cost_center_ids: null,
      }).order("window_no").order("account_id").range(from, from + REPORT_QUERY_PAGE_SIZE - 1);
      if (error) throw error;
      const rows = (data || []) as any[];
      all.push(...rows);
      if (rows.length < REPORT_QUERY_PAGE_SIZE) break;
    }
    const byWin = new Map<number, any[]>();
    for (const r of all) { const a = byWin.get(r.window_no) || []; a.push(r); byWin.set(r.window_no, a); }
    for (const it of items) it.resolve(byWin.get(keys.indexOf(it.key) + 1) || []);
  } catch (e) {
    for (const it of items) it.reject(e);
  }
}

function batchedTotalsRows(companyId: string, from: string | null, to: string | null): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const list = pendingByCompany.get(companyId) || [];
    if (!list.length) setTimeout(() => { void flushTotalsBatch(companyId); }, 0);
    list.push({ key: `${from ?? ""}|${to ?? ""}`, from, to, resolve, reject });
    pendingByCompany.set(companyId, list);
  });
}

export async function getAccountTotals(opts: {
  companyId: string;
  dateFrom: string | null;
  dateTo: string | null;
  costCenterIds?: string[] | null;
}): Promise<Map<string, AccountTotals>> {
  const noCc = !(opts.costCenterIds && opts.costCenterIds.length);
  const rows = noCc && totalsBatchCompanies.has(opts.companyId)
    ? await batchedTotalsRows(opts.companyId, opts.dateFrom ?? null, opts.dateTo ?? null)
    : await rpcAll<any>("rpc_account_totals_for_report", {
    p_company_id: opts.companyId,
    p_date_from: opts.dateFrom ?? null,
    p_date_to: opts.dateTo ?? null,
    p_cost_center_ids: opts.costCenterIds && opts.costCenterIds.length ? opts.costCenterIds : null,
  });

  const map = new Map<string, AccountTotals>();
  for (const r of rows) {
    if (!r.account_id) continue;
    map.set(r.account_id, {
      opening_debit: numeric(r.opening_debit),
      opening_credit: numeric(r.opening_credit),
      period_debit: numeric(r.period_debit),
      period_credit: numeric(r.period_credit),
    });
  }
  return map;
}

/**
 * Drop opening-balance lines dated on/before dateFrom: rpc_account_totals_for_report
 * already counts them in opening_debit/credit, so showing them as movements doubles them.
 */
export function excludeOpeningCountedLines<T extends { source?: string | null; entry_date: string }>(
  lines: T[],
  dateFrom: string | null | undefined,
): T[] {
  if (!dateFrom) return lines;
  return lines.filter((l) => !(l.source === "opening_balance" && l.entry_date <= dateFrom));
}

export async function getLedgerLines(opts: LinesQuery): Promise<LedgerLine[]> {
  // Ordering (entry_date, entry_number, line_no) is applied server-side.
  return excludeOpeningCountedLines(await fetchLines(opts), opts.dateFrom);
}


/**
 * Get account totals bucketed by cost center.
 *
 * `buckets` is an ordered list of cost-center ids the user picked. For each
 * picked bucket we sum lines whose `cost_center_id` is the bucket itself OR
 * any descendant of it (so a picked group aggregates all its sub-centers).
 * Lines that do not match any bucket fall into the special `__unalloc__`
 * key. Bucket ids are returned in the same order as `buckets`, with
 * `__unalloc__` last (only when non-empty).
 */
export async function getAccountTotalsByCostCenter(opts: {
  companyId: string;
  dateFrom: string | null;
  dateTo: string | null;
  buckets: string[]; // raw picked cost-center ids
  costCenterParents: Map<string, string | null>; // id -> parent_id
}): Promise<{
  byAccount: Map<string, Map<string, AccountTotals>>;
  bucketIds: string[]; // ordered, may include "__unalloc__"
}> {
  // Pre-compute, for each cost center, the set of picked bucket ids that
  // contain it (i.e. ancestors that are in `buckets`). A leaf can belong to
  // multiple buckets only if the user picked overlapping groups; in a tree
  // that means a group and one of its ancestors — we add the line to BOTH
  // so column subtotals reflect the user's intent. The "Total" column on
  // screen re-sums non-overlapping picked roots, so do not include parents
  // automatically.
  const pickedSet = new Set(opts.buckets);
  const memo = new Map<string, string[]>();
  const ancestorsInPicked = (id: string): string[] => {
    if (memo.has(id)) return memo.get(id)!;
    const out: string[] = [];
    let cur: string | null | undefined = id;
    while (cur) {
      if (pickedSet.has(cur)) out.push(cur);
      cur = opts.costCenterParents.get(cur) ?? null;
    }
    memo.set(id, out);
    return out;
  };

  const sawUnalloc = { v: false };
  const byAccount = new Map<string, Map<string, AccountTotals>>();
  const empty2 = (): AccountTotals => ({ opening_debit: 0, opening_credit: 0, period_debit: 0, period_credit: 0 });

  // One aggregated round-trip: totals per (account, cost center), already split
  // into opening (< dateFrom) and period by the RPC.
  const rows = await rpcAll<any>("rpc_account_totals_by_cost_center", {
    p_company_id: opts.companyId,
    p_date_from: opts.dateFrom ?? null,
    p_date_to: opts.dateTo ?? null,
  });

  for (const r of rows) {
    if (!r.account_id) continue;
    const ccId: string | null = r.cost_center_id ?? null;
    const bucketIds = ccId ? ancestorsInPicked(ccId) : [];
    const targets = bucketIds.length ? bucketIds : ["__unalloc__"];
    if (!bucketIds.length) sawUnalloc.v = true;
    let perAcc = byAccount.get(r.account_id);
    if (!perAcc) { perAcc = new Map(); byAccount.set(r.account_id, perAcc); }
    for (const b of targets) {
      let t = perAcc.get(b);
      if (!t) { t = empty2(); perAcc.set(b, t); }
      t.opening_debit  += numeric(r.opening_debit);
      t.opening_credit += numeric(r.opening_credit);
      t.period_debit   += numeric(r.period_debit);
      t.period_credit  += numeric(r.period_credit);
    }
  }

  const bucketIds = [...opts.buckets];
  if (sawUnalloc.v) bucketIds.push("__unalloc__");
  return { byAccount, bucketIds };
}

